import { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  selectLayoutUser,
  selectCurrentProject,
  selectCurrentSite,
  selectInstanceInfo,
  selectMenuBadges
} from '@/entities/layout';
import {
  createNavigationItemsWithStateSelector,
  selectIsCollapsed,
  selectIsMobileOpen,
  toggleItem,
  toggleSidebar,
  setMobileSidebarOpen,
  type NavigationItemWithState
} from '@/entities/navigation';
import { useIsMobile } from '@/hooks/use-mobile';
import { useProjectSiteParams } from '@/shared/lib';
import type { AppDispatch, RootState } from '@/app/types/store';

export const useSidebar = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useIsMobile();
  const { projectId, siteId } = useProjectSiteParams();

  const currentProjectSelector = useMemo(
    () => (projectId && siteId ? selectCurrentProject(projectId, siteId) : () => null),
    [projectId, siteId]
  );

  const currentSiteSelector = useMemo(
    () => (projectId && siteId ? selectCurrentSite(projectId, siteId) : () => null),
    [projectId, siteId]
  );

  const instanceInfoSelector = useMemo(
    () => (projectId && siteId ? selectInstanceInfo(projectId, siteId) : () => null),
    [projectId, siteId]
  );

  const badgesSelector = useMemo(
    () => (projectId && siteId ? selectMenuBadges(projectId, siteId) : () => null),
    [projectId, siteId]
  );

  const user = useSelector(selectLayoutUser);
  const currentProject = useSelector(currentProjectSelector);
  const currentSite = useSelector(currentSiteSelector);
  const instanceInfo = useSelector(instanceInfoSelector);
  const badges = useSelector(badgesSelector);
  const isCollapsed = useSelector(selectIsCollapsed);
  const isMobileOpen = useSelector(selectIsMobileOpen);

  const navigationItemsSelector = useMemo(
    () => createNavigationItemsWithStateSelector(projectId, siteId),
    [projectId, siteId]
  );

  const navigationItems = useSelector((state: RootState) => navigationItemsSelector(state, location.pathname));

  useEffect(() => {
    if (isMobile && isMobileOpen) {
      dispatch(setMobileSidebarOpen(false));
    }
  }, [location.pathname, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  const itemsWithBadges = useMemo(
    () =>
      navigationItems.map((item) => ({
        ...item,
        badge: badges?.[item.code as keyof typeof badges] || item.badge
      })) as NavigationItemWithState[],
    [navigationItems, badges]
  );

  const handleToggleItem = (code: string) => {
    dispatch(toggleItem(code));
  };

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  const handleOpenMobile = () => {
    dispatch(setMobileSidebarOpen(true));
  };

  const handleCloseMobile = () => {
    dispatch(setMobileSidebarOpen(false));
  };

  return {
    // State
    user,
    currentProject,
    currentSite,
    instanceInfo,
    isCollapsed: isMobile ? false : isCollapsed,
    isMobileOpen,
    isMobile,
    items: itemsWithBadges,

    // Actions
    onToggleItem: handleToggleItem,
    onToggleSidebar: handleToggleSidebar,
    onOpenMobile: handleOpenMobile,
    onCloseMobile: handleCloseMobile
  };
};
